import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles, Theme, Typography } from '@material-ui/core'
import HistoryIcon from '@material-ui/icons/History'
import type { SpecIntelligenceReportHeader } from 'api/graphQL/types/specIntelligence.types'
import clsx from 'clsx'
import React from 'react'
import { formatLocalDateTime } from 'shared/utils/dateUtils'

const RUNS_LIST_WIDTH_PX = 268

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        width: pxToRem(RUNS_LIST_WIDTH_PX),
        flexShrink: 0,
        backgroundColor: '#FCFDFF',
        borderRight: `1px solid ${theme.palette.divider}`,
        overflowY: 'auto',
    },
    header: {
        position: 'sticky',
        top: 0,
        backgroundColor: '#FCFDFF',
        padding: theme.spacing(1.5, 2, 1.25),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    headerTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: pxToRem(6),
        fontSize: pxToRem(15),
        fontWeight: 700,
        color: '#043852',
    },
    headerTitleIcon: {
        fontSize: pxToRem(18),
    },
    headerMeta: {
        fontSize: pxToRem(12),
        color: '#595959',
        marginTop: pxToRem(4),
    },
    list: {
        padding: theme.spacing(1),
    },
    row: {
        padding: theme.spacing(1.5),
        marginBottom: pxToRem(4),
        borderRadius: pxToRem(8),
        borderLeft: '3px solid transparent',
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#F3F5F9' },
    },
    rowSelected: {
        backgroundColor: '#EEF4FB',
        borderLeft: '3px solid #043852',
        '&:hover': { backgroundColor: '#EEF4FB' },
    },
    runDate: {
        fontSize: pxToRem(13),
        fontWeight: 600,
        color: '#212121',
    },
    runMeta: {
        fontSize: pxToRem(11),
        color: '#595959',
        marginTop: pxToRem(2),
    },
}))

/** Props for {@link InsightsRunsList}. */
export interface InsightsRunsListProps {
    /** Run headers, expected newest-first. */
    runs: SpecIntelligenceReportHeader[]
    /** The currently selected run's id, if any. */
    selectedId: string | null
    /** Called with a run's id when the user clicks its row. */
    onSelect: (id: string) => void
}

/**
 * Left-hand list of past SpecIntelligence runs for the Insights sub-tab.
 * Purely presentational — selection state is owned by the parent.
 */
const InsightsRunsList = ({ runs, selectedId, onSelect }: InsightsRunsListProps) => {
    const classes = useStyles()

    return (
        <div className={classes.container} data-testid="insightsRunsList">
            <div className={classes.header}>
                <div className={classes.headerTitle}>
                    <HistoryIcon className={classes.headerTitleIcon} />
                    Insights Runs
                </div>
                <div className={classes.headerMeta}>
                    {runs.length} run{runs.length === 1 ? '' : 's'}
                    {runs.length > 0 && ` · Last ${formatLocalDateTime(new Date(runs[0].runDate))}`}
                </div>
            </div>
            <div className={classes.list}>
                {runs.map((run) => (
                    <div
                        key={run.id}
                        className={clsx(classes.row, run.id === selectedId && classes.rowSelected)}
                        onClick={() => onSelect(run.id)}
                        role="button"
                        tabIndex={0}
                        aria-current={run.id === selectedId}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                onSelect(run.id)
                            }
                        }}
                    >
                        <Typography component="div" className={classes.runDate}>
                            {formatLocalDateTime(new Date(run.runDate))}
                        </Typography>
                        <Typography component="div" className={classes.runMeta}>
                            By {run.runByName} · {run.sectionCount} sections · {run.findingCount} findings
                        </Typography>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default InsightsRunsList
